import React, { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import { selectCallMinimalMode } from '@app/model/app-settings';
import { useAppSelector } from '@app/store/hooks';
import { useHotkeys } from '@shared/hooks';
import { isMedia } from '@shared/lib/html';
import { Button } from '@shared/ui/button';
import { DropDown } from '@shared/ui/dropdown';
import { GlobalPortal } from '@shared/ui/global-portal';
import { InfoModal } from '@entities/info-modal';
import IconChevronDown from '@assets/img/icons/chevron-down.svg';
import IconChevronUp from '@assets/img/icons/chevron-up.svg';
import IconClockPast from '@assets/img/icons/clock-past.svg';
import IconX from '@assets/img/icons/close-x.svg';
import IconMic from '@assets/img/icons/mic.svg';
import IconMicOff from '@assets/img/icons/mic-off-colored.svg';
import IconMoreKebab from '@assets/img/icons/more-horizontal-kebab.svg';
import IconPause from '@assets/img/icons/pause-circle.svg';
import IconPhoneEnd from '@assets/img/icons/phone-end.svg';
import IconPhonePark from '@assets/img/icons/phone-park.svg';
import IconPlay from '@assets/img/icons/play.svg';
import IconSettings from '@assets/img/icons/settings.svg';
import IconShareScreen from '@assets/img/icons/share-screen.svg';
import IconUserMany from '@assets/img/icons/user-many.svg';
import IconUserMove from '@assets/img/icons/user-move.svg';
import IconVideo from '@assets/img/icons/video.svg';
import IconVideoOff from '@assets/img/icons/video-off-colored.svg';

import { useDeviceSettings, useMediaDevices } from '../../hooks';
import { exitModalLabels, parkingModalLabels } from './config';
import { DeviceItem } from './device-item';
import { CallControlsProps, ControlKey, DropdownType, HotkeyCombo } from './types';

import styles from './styles.module.scss';

const DISPLAY_HOTKEYS = {
	audio: ['CTRL', 'D'] as const,
	video: ['CTRL', 'E'] as const,
};

const HOTKEYS: Record<'audio' | 'video', ReadonlyArray<HotkeyCombo>> = {
	audio: [DISPLAY_HOTKEYS.audio],
	video: [DISPLAY_HOTKEYS.video],
};

const renderHotkeyHint = (label: string, hotkeys: ReadonlyArray<string>) => (
	<>
		{label}
		{hotkeys.map((hotkey) => (
			<span key={hotkey}>{hotkey}</span>
		))}
	</>
);

export const CallControls: FC<CallControlsProps> = ({
	currentCall,
	isLoadingRequest,
	isOpenConferencePanel,
	onAudioToggle,
	onHangup,
	onHold,
	onParkingCallClick,
	onCreateConferenceClick,
	onResume,
	onShareScreen,
	onToggleShowPanel,
	onTransferClick,
	onVideoToggle,
}) => {
	const isMinimalMode = useAppSelector(selectCallMinimalMode);
	const [activeDropdown, setActiveDropdown] = useState<DropdownType>(null);
	const [showExitModal, setShowExitModal] = useState(false);
	const [showParkingModal, setShowParkingModal] = useState(false);
	const [queuedVideoToggle, setQueuedVideoToggle] = useState(false);
	const [controlLoading, setControlLoading] = useState<Record<ControlKey, boolean>>({
		audio: false,
		video: false,
		share: false,
	});

	const refMicBtn = useRef<HTMLButtonElement>(null);
	const refVideoBtn = useRef<HTMLButtonElement>(null);
	const refAdditionalBtn = useRef<HTMLButtonElement>(null);
	const refDropdown = useRef<HTMLDivElement>(null);

	const { audioInputs, audioOutputs, videoInputs } = useMediaDevices();

	const {
		selectedAudioInput,
		selectedVideoInput,
		selectedAudioOutput,
		handleAudioInputChange,
		handleVideoInputChange,
		handleAudioOutputChange,
	} = useDeviceSettings();

	const isMobile = isMedia('sm');
	const { state, local: localMedia, screenShare: isScreenSharing, isConference } = currentCall;
	/** Флаг когда звоним */
	const isCalling = state === 'calling';
	/** Флаг удержания звонка */
	const isRemoteHold = state === 'remoteHold';
	const isLocalHold = state === 'hold';
	/** Флаг звонка на парковке */
	const isParkedCall = state === 'parked';

	/** Флаг включенного аудио */
	const isAudioEnabled = !!localMedia?.audio;
	/** Флаг включенного видео */
	const isVideoEnabled = !!localMedia?.video;

	const isDisabledAudioButton = isLoadingRequest || isParkedCall;
	const isDisabledButton = isDisabledAudioButton || isRemoteHold;
	const audioButtonLoading = controlLoading.audio || isDisabledAudioButton;
	const videoButtonLoading = controlLoading.video || isDisabledButton;
	const shareButtonLoading = controlLoading.share || isDisabledButton;
	const audioHintLabel = isAudioEnabled ? 'Выключить микрофон' : 'Включить микрофон';
	const videoHintLabel = isVideoEnabled ? 'Выключить камеру' : 'Включить камеру';
	const audioHint = renderHotkeyHint(audioHintLabel, DISPLAY_HOTKEYS.audio);
	const videoHint = renderHotkeyHint(videoHintLabel, DISPLAY_HOTKEYS.video);

	const toggleDropdown = (type: DropdownType) => (e: React.MouseEvent) => {
		e.stopPropagation();
		setActiveDropdown((prev) => (prev === type ? null : type));
	};

	const toggleExitModal = () => {
		setShowExitModal((s) => !s);
	};

	const toggleParkingModal = () => {
		if (isDisabledButton) return;
		setActiveDropdown(null);
		setShowParkingModal((s) => !s);
	};

	const toggleShowPanel = () => {
		setActiveDropdown(null);
		onToggleShowPanel();
	};

	const updateControlLoading = useCallback((key: ControlKey, value: boolean) => {
		setControlLoading((prev) => {
			if (prev[key] === value) return prev;
			return { ...prev, [key]: value };
		});
	}, []);

	const handleClickOutside = (e: MouseEvent) => {
		const target = e.target as Node;
		const refs = [refMicBtn, refVideoBtn, refAdditionalBtn, refDropdown];
		if (!refs.some((r) => r.current?.contains(target))) {
			setActiveDropdown(null);
		}
	};

	const handleHangUp = () => {
		toggleExitModal();
		onHangup();
	};

	const handleAudioToggle = useCallback(() => {
		if (isRemoteHold) {
			onAudioToggle();
			return;
		}
		if (isDisabledAudioButton || controlLoading.audio) return;
		updateControlLoading('audio', true);
		onAudioToggle();
	}, [
		controlLoading.audio,
		isDisabledAudioButton,
		isRemoteHold,
		onAudioToggle,
		updateControlLoading,
	]);

	const runVideoToggle = useCallback(() => {
		updateControlLoading('video', true);

		Promise.resolve(onVideoToggle()).finally(() => {
			updateControlLoading('video', false);
		});
	}, [onVideoToggle, updateControlLoading]);

	const handleVideoToggle = useCallback(() => {
		if (isScreenSharing) return;
		if (isDisabledButton || controlLoading.video) {
			setQueuedVideoToggle(true);
			return;
		}

		runVideoToggle();
	}, [controlLoading.video, isDisabledButton, isScreenSharing, runVideoToggle]);

	const handleShareScreen = useCallback(async () => {
		if (isDisabledButton || controlLoading.share) return;

		updateControlLoading('share', true);

		try {
			await Promise.resolve(onShareScreen());
		} finally {
			updateControlLoading('share', false);
		}
	}, [controlLoading.share, isDisabledButton, onShareScreen, updateControlLoading]);

	const handleTransferClick = () => {
		if (isDisabledButton) return;
		setActiveDropdown(null);
		onTransferClick();
	};

	const handleParkingCallClick = () => {
		onParkingCallClick();
		toggleParkingModal();
	};

	const handleCreateConferenceClick =
		(isOpenDialPad = false) =>
		() => {
			if (isDisabledButton) return;
			setActiveDropdown(null);
			onCreateConferenceClick(isOpenDialPad);
		};

	const handleHoldClick = () => {
		if (isDisabledButton) return;
		setActiveDropdown(null);
		onHold();
	};

	const handleResumeClick = () => {
		if (isDisabledButton) return;
		setActiveDropdown(null);
		onResume();
	};

	useEffect(() => {
		setControlLoading((prev) => {
			const next = {
				audio: isDisabledAudioButton || isRemoteHold ? false : prev.audio,
				video: isDisabledButton ? false : prev.video,
				share: isDisabledButton ? false : prev.share,
			};

			if (next.audio === prev.audio && next.video === prev.video && next.share === prev.share) {
				return prev;
			}

			return next;
		});
	}, [isDisabledAudioButton, isDisabledButton, isRemoteHold]);

	useEffect(() => {
		if (!queuedVideoToggle) return;
		if (isDisabledButton || isScreenSharing || controlLoading.video) return;

		setQueuedVideoToggle(false);
		runVideoToggle();
	}, [controlLoading.video, isDisabledButton, isScreenSharing, queuedVideoToggle, runVideoToggle]);

	const hotkeyHandlers = useMemo(
		() => [
			{ hotkey: HOTKEYS.audio, callback: handleAudioToggle },
			{ hotkey: HOTKEYS.video, callback: handleVideoToggle },
		],
		[handleAudioToggle, handleVideoToggle]
	);

	useHotkeys(hotkeyHandlers);

	useEffect(() => {
		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, [handleClickOutside]);

	const renderDeviceList = (
		title: string,
		items: MediaDeviceInfo[],
		selected: string,
		onChange: (id: string) => void
	) => {
		const handleChange = (id: string) => {
			setActiveDropdown(null);
			onChange(id);
		};

		return (
			<>
				<div className={styles.DropDownTitle}>{title}</div>
				{items.map((item) => (
					<DeviceItem
						key={item.deviceId}
						option={item}
						selectedId={selected}
						onChange={handleChange}
					/>
				))}
			</>
		);
	};

	const renderMicDropdown = () => (
		<DropDown ref={refDropdown} offsetTop={-14} refParent={refMicBtn}>
			<div className={styles.DropDown}>
				{renderDeviceList(
					'Выберите микрофон',
					audioInputs,
					selectedAudioInput,
					handleAudioInputChange
				)}
				<div className={styles.Separator} />
				{renderDeviceList(
					'Выберите динамик',
					audioOutputs,
					selectedAudioOutput,
					handleAudioOutputChange
				)}
				<div className={styles.Separator} />
				<div className={cn(styles.DropDownItem, styles.SettingsWrapper)}>
					<div className={styles.SettingsIcon} dangerouslySetInnerHTML={{ __html: IconSettings }} />
					Открыть настройки аудио...
				</div>
			</div>
		</DropDown>
	);

	const renderVideoDropdown = () => (
		<DropDown ref={refDropdown} offsetTop={-14} refParent={refVideoBtn}>
			<div className={styles.DropDown}>
				{renderDeviceList(
					'Выберите камеру',
					videoInputs,
					selectedVideoInput,
					handleVideoInputChange
				)}
				<div className={styles.Separator} />
				<div className={cn(styles.DropDownItem, styles.SettingsWrapper)}>
					<div className={styles.SettingsIcon} dangerouslySetInnerHTML={{ __html: IconSettings }} />
					Открыть настройки видео...
				</div>
			</div>
		</DropDown>
	);

	const additionalActions = (
		<>
			{!isConference && (
				<div
					className={cn(styles.AdditionalItem, styles.SettingsWrapper)}
					onClick={handleHoldClick}
				>
					<div className={styles.SettingsIcon} dangerouslySetInnerHTML={{ __html: IconPause }} />
					Поставить на удержание
				</div>
			)}
			{!isConference && (
				<div
					className={cn(styles.AdditionalItem, styles.SettingsWrapper)}
					onClick={handleTransferClick}
				>
					<div className={styles.SettingsIcon} dangerouslySetInnerHTML={{ __html: IconUserMove }} />
					Перевести звонок
				</div>
			)}
			{!isConference && (
				<div
					className={cn(styles.AdditionalItem, styles.SettingsWrapper)}
					onClick={toggleParkingModal}
				>
					<div
						className={styles.SettingsIcon}
						dangerouslySetInnerHTML={{ __html: IconPhonePark }}
					/>
					Припарковать звонок
				</div>
			)}
			<div
				className={cn(styles.AdditionalItem, styles.SettingsWrapper)}
				onClick={handleCreateConferenceClick(true)}
			>
				<div className={styles.SettingsIcon} dangerouslySetInnerHTML={{ __html: IconUserMove }} />
				Добавить участника
			</div>
			{isMobile && (
				<div
					className={cn(styles.AdditionalItem, styles.SettingsWrapper)}
					onClick={toggleShowPanel}
				>
					<div
						className={styles.SettingsIcon}
						dangerouslySetInnerHTML={{ __html: IconClockPast }}
					/>
					История звонков
				</div>
			)}
		</>
	);

	const renderAdditionalDropdown = () =>
		isMobile ? (
			<GlobalPortal>
				<div ref={refDropdown} className={styles.Popup}>
					<div className={styles.PopupHeader}>
						<div className={styles.PopupHeaderText}>Дополнительные действия</div>
						<Button
							buttonType="icon"
							className={styles.PopupClose}
							svg={IconX}
							onClick={toggleDropdown('additional')}
						/>
					</div>
					{additionalActions}
				</div>
			</GlobalPortal>
		) : (
			<DropDown ref={refDropdown} offsetTop={-14} refParent={refAdditionalBtn}>
				<div className={styles.DropDown}>{additionalActions}</div>
			</DropDown>
		);

	if (isMinimalMode) {
		return (
			<div className={styles.Controls}>
				<div className={styles.ControlsDropDown}>
					<Button
						buttonType="icon"
						className={cn({ [styles.ActiveBtn]: isAudioEnabled })}
						hint={audioHint}
						isLoading={audioButtonLoading}
						svg={isAudioEnabled ? IconMic : IconMicOff}
						onClick={handleAudioToggle}
					/>
					<Button
						ref={refMicBtn}
						buttonType="icon-mini"
						svg={activeDropdown === 'mic' ? IconChevronDown : IconChevronUp}
						onClick={toggleDropdown('mic')}
					/>
					{activeDropdown === 'mic' && renderMicDropdown()}
				</div>

				{isMobile && (
					<Button
						buttonType="icon"
						hint="История звонков"
						svg={IconClockPast}
						onClick={onToggleShowPanel}
					/>
				)}

				<Button
					buttonType="icon-primary"
					className={styles.EndCall}
					hint="Завершить звонок"
					svg={IconPhoneEnd}
					onClick={() => onHangup()}
				/>
			</div>
		);
	}

	return (
		<div className={styles.Controls}>
			{isLocalHold ? (
				<>
					<Button svg={IconPlay} onClick={handleResumeClick}>
						Возобновить звонок
					</Button>

					{isMobile && (
						<Button
							buttonType="icon"
							hint="История звонков"
							svg={IconClockPast}
							onClick={onToggleShowPanel}
						/>
					)}
				</>
			) : (
				<>
					{/* MIC */}
					<div className={styles.ControlsDropDown}>
						<Button
							buttonType="icon"
							className={cn({ [styles.ActiveBtn]: isAudioEnabled })}
							hint={audioHint}
							isLoading={audioButtonLoading}
							svg={isAudioEnabled ? IconMic : IconMicOff}
							onClick={handleAudioToggle}
						/>
						<Button
							ref={refMicBtn}
							buttonType="icon-mini"
							svg={activeDropdown === 'mic' ? IconChevronDown : IconChevronUp}
							onClick={toggleDropdown('mic')}
						/>
						{activeDropdown === 'mic' && renderMicDropdown()}
					</div>
					{/* VIDEO */}
					<div className={styles.ControlsDropDown}>
						<Button
							buttonType="icon"
							className={cn({ [styles.ActiveBtn]: isVideoEnabled })}
							disabled={isScreenSharing}
							hint={videoHint}
							isLoading={videoButtonLoading}
							svg={isVideoEnabled ? IconVideo : IconVideoOff}
							onClick={handleVideoToggle}
						/>
						<Button
							ref={refVideoBtn}
							buttonType="icon-mini"
							svg={activeDropdown === 'video' ? IconChevronDown : IconChevronUp}
							onClick={toggleDropdown('video')}
						/>
						{activeDropdown === 'video' && renderVideoDropdown()}
					</div>

					{/* SCREEN SHARE */}
					{!isMobile && (
						<Button
							buttonType="icon"
							className={cn({ [styles.ActiveBtn]: isScreenSharing })}
							disabled={isVideoEnabled}
							hint={isScreenSharing ? 'Прекратить трансляцию' : 'Начать трансляцию'}
							isLoading={shareButtonLoading}
							svg={IconShareScreen}
							onClick={handleShareScreen}
						/>
					)}

					{!isCalling && isConference && (
						<Button
							buttonType="icon"
							className={cn({ [styles.ActiveBtn]: isOpenConferencePanel })}
							svg={IconUserMany}
							onClick={handleCreateConferenceClick()}
						/>
					)}

					{isCalling && isMobile && (
						<Button
							buttonType="icon"
							hint="История звонков"
							svg={IconClockPast}
							onClick={toggleShowPanel}
						/>
					)}

					{!isCalling && (
						<Button
							ref={refAdditionalBtn}
							buttonType="icon"
							hint="Дополнительные действия"
							svg={IconMoreKebab}
							onClick={toggleDropdown('additional')}
						/>
					)}
					{activeDropdown === 'additional' && renderAdditionalDropdown()}
				</>
			)}

			{/* END CALL */}
			<Button
				buttonType="icon-primary"
				className={styles.EndCall}
				hint="Завершить звонок"
				svg={IconPhoneEnd}
				onClick={() => onHangup()}
			/>

			<InfoModal
				handleApply={handleHangUp}
				modalInfo={exitModalLabels}
				setShowModal={setShowExitModal}
				showModal={showExitModal}
				title="Завершение звонка"
			/>
			<InfoModal
				handleApply={handleParkingCallClick}
				modalInfo={parkingModalLabels}
				setShowModal={setShowParkingModal}
				showModal={showParkingModal}
				title="Парковка звонка"
			/>
		</div>
	);
};
